import { useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from '@flote/ui';
import {
  Plus,
  User,
  Phone,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  BellOff,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { deliveryTsr } from '../../api';
import { RecipientFormModal } from '../../components/recipients';
import { getErrorMessage } from '../../lib/error-utils';
import type { Recipient, CreateRecipientRequest, UpdateRecipientRequest } from '@flote/contracts/http';

const PAGE_SIZE = 10;

export function RecipientsPage() {
  // Filter state
  const [nameFilter, setNameFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [appliedPhoneFilter, setAppliedPhoneFilter] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // List recipients query
  const listQuery = deliveryTsr.adminRecipients.list.useQuery({
    queryKey: ['recipients', appliedNameFilter, appliedPhoneFilter],
    queryData: {
      query: {
        name: appliedNameFilter || undefined,
        phone: appliedPhoneFilter || undefined,
        limit: 500,
      },
    },
  });

  const allRecipients = listQuery.data?.status === 200 ? listQuery.data.body : [];

  // Client-side pagination
  const totalPages = Math.ceil(allRecipients.length / PAGE_SIZE);
  const paginatedRecipients = allRecipients.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Mutations
  const createMutation = deliveryTsr.adminRecipients.create.useMutation();
  const updateMutation = deliveryTsr.adminRecipients.update.useMutation();
  const syncMutation = deliveryTsr.adminRecipients.sync.useMutation();

  const handleCreate = useCallback(async (data: CreateRecipientRequest | UpdateRecipientRequest) => {
    setIsProcessing(true);

    try {
      const result = await createMutation.mutateAsync({
        body: data as CreateRecipientRequest,
      });

      if (result.status === 201) {
        toast.success('Recipient created successfully');
        setIsCreateModalOpen(false);
        listQuery.refetch();
      } else {
        toast.error('Failed to create recipient');
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error(getErrorMessage(error, 'Failed to create recipient'));
    } finally {
      setIsProcessing(false);
    }
  }, [createMutation, listQuery]);

  const handleUpdate = useCallback(async (data: CreateRecipientRequest | UpdateRecipientRequest) => {
    if (!selectedRecipient) return;

    setIsProcessing(true);

    try {
      const result = await updateMutation.mutateAsync({
        params: { recipientId: selectedRecipient.id },
        body: data as UpdateRecipientRequest,
      });

      if (result.status === 200) {
        toast.success('Recipient updated successfully');
        setIsEditModalOpen(false);
        setSelectedRecipient(null);
        listQuery.refetch();
      } else {
        toast.error('Failed to update recipient');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(getErrorMessage(error, 'Failed to update recipient'));
    } finally {
      setIsProcessing(false);
    }
  }, [selectedRecipient, updateMutation, listQuery]);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await syncMutation.mutateAsync({
        body: {},
      });

      if (response.status === 200) {
        toast.success(`${response.body.synced} recipients in cache`);
        listQuery.refetch();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to sync recipients');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync recipients'));
      console.error('Sync recipients error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [syncMutation, listQuery]);

  const handleEditClick = (recipient: Recipient) => {
    setSelectedRecipient(recipient);
    setIsEditModalOpen(true);
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedNameFilter(nameFilter);
    setAppliedPhoneFilter(phoneFilter);
    setPage(1);
  };

  const clearFilters = () => {
    setNameFilter('');
    setPhoneFilter('');
    setAppliedNameFilter('');
    setAppliedPhoneFilter('');
    setPage(1);
  };

  const hasFilters = appliedNameFilter || appliedPhoneFilter;

  const formatPhoneDisplay = (phone: string): string => {
    // If already formatted or international, return as-is
    if (phone.includes('(') || phone.startsWith('+')) {
      return phone;
    }
    // Try to format US numbers
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Recipients</h1>
          <p className="text-sm text-muted-foreground">
            Manage delivery recipients and their contact information
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing}
            className="w-full sm:w-auto"
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync Cache
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Create Recipient
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={applyFilters} className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="nameFilter">Name</Label>
              <Input
                id="nameFilter"
                placeholder="Filter by name..."
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="phoneFilter">Phone</Label>
              <Input
                id="phoneFilter"
                placeholder="Filter by phone..."
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 sm:flex-none">
                Apply
              </Button>
              {hasFilters && (
                <Button type="button" variant="outline" onClick={clearFilters} className="flex-1 sm:flex-none">
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Recipients Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Recipients
              {allRecipients.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({allRecipients.length} total)
                </span>
              )}
            </CardTitle>
            {listQuery.isFetching && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : allRecipients.length === 0 ? (
            <div className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-sm font-medium">No recipients found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {hasFilters
                  ? 'Try adjusting your filters or create a new recipient.'
                  : 'Get started by creating your first recipient.'}
              </p>
              <Button className="mt-4" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Recipient
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[120px]">Created</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRecipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium">{recipient.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-mono text-sm">
                              {formatPhoneDisplay(recipient.phone)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {recipient.notes ? (
                            <span className="text-sm text-muted-foreground line-clamp-1">
                              {recipient.notes}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {recipient.skipSMSNotifications ? (
                            <Badge variant="secondary" className="gap-1">
                              <BellOff className="h-3 w-3" />
                              SMS Off
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              SMS On
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {new Date(recipient.createdAt).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(recipient)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(page * PAGE_SIZE, allRecipients.length)} of{' '}
                    {allRecipients.length} recipients
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <RecipientFormModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreate}
        isProcessing={isProcessing}
      />

      {/* Edit Modal */}
      {selectedRecipient && (
        <RecipientFormModal
          open={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open);
            if (!open) setSelectedRecipient(null);
          }}
          onSubmit={handleUpdate}
          isProcessing={isProcessing}
          recipient={selectedRecipient}
        />
      )}
    </div>
  );
}
